import { Link, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import {
  collection,
  query,
  where,
  getCountFromServer,
  orderBy,
  limit,
  getDocs,
} from "firebase/firestore";
import { db } from "../../Firebase";
import StatusSelect from "../../components/StatusSelect";
import AdminNav from "../../components/AdminNav";

function safeNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function isOutstandingInvoice(inv) {
  const status = String(inv?.status || "open").trim().toLowerCase();
  if (status === "paid" || status === "void") return false;

  const balanceDue = safeNum(inv?.totals?.balanceDue ?? inv?.balanceDue);
  return balanceDue > 0;
}

export default function AdminDashboard() {
  const [leadCount, setLeadCount] = useState(null);
  const [newLeadCount, setNewLeadCount] = useState(null);
  const [recentLeads, setRecentLeads] = useState([]);
  const [contactedCount, setContactedCount] = useState(null);

  // NEW: Active estimates
  const [activeEstimatesCount, setActiveEstimatesCount] = useState(null);
  const [activeEstimates, setActiveEstimates] = useState([]);

  // NEW: Active invoices
  const [activeInvoicesCount, setActiveInvoicesCount] = useState(null);
  const [activeInvoices, setActiveInvoices] = useState([]);
  const [totalOutstanding, setTotalOutstanding] = useState(0);

  // NEW: Expenses
  const [unpaidExpensesCount, setUnpaidExpensesCount] = useState(null);
  const [unpaidExpenses, setUnpaidExpenses] = useState([]);
  const [totalUnpaidExpenses, setTotalUnpaidExpenses] = useState(0);

  const navigate = useNavigate();

  const money = useMemo(() => {
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: "USD",
      });
    } catch {
      return { format: (n) => `$${Number(n || 0).toFixed(2)}` };
    }
  }, []);

  useEffect(() => {
    const fetchDashboardData = async () => {
      // --------------------
      // LEADS (existing)
      // --------------------
      const allLeadsSnap = await getCountFromServer(collection(db, "leads"));

      const newLeadsSnap = await getCountFromServer(
        query(collection(db, "leads"), where("status", "==", "new"))
      );

      const contactedSnap = await getCountFromServer(
        query(collection(db, "leads"), where("status", "==", "contacted"))
      );

      // Recent leads (last 5)
      const recentQuery = query(
        collection(db, "leads"),
        orderBy("createdAt", "desc"),
        limit(5)
      );
      const recentSnap = await getDocs(recentQuery);
      const recent = recentSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setLeadCount(allLeadsSnap.data().count);
      setNewLeadCount(newLeadsSnap.data().count);
      setContactedCount(contactedSnap.data().count);
      setRecentLeads(recent);

      // --------------------
      // ACTIVE ESTIMATES (publicEstimates)
      // active if status in ["sent","viewed"]
      // --------------------
      const activeEstimateStatuses = ["sent", "viewed"];

      const activeEstimatesQ = query(
        collection(db, "publicEstimates"),
        where("status", "in", activeEstimateStatuses),
        orderBy("createdAt", "desc") // if you don't have createdAt, swap to sentAt/updatedAt or remove this line
      );

      let estimates = [];
      try {
        const activeEstimatesSnap = await getDocs(activeEstimatesQ);
        estimates = activeEstimatesSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
      } catch (err) {
        // If you don't have createdAt (or no index), you can remove orderBy and retry.
        console.error("Active estimates query failed:", err);
        const fallbackQ = query(
          collection(db, "publicEstimates"),
          where("status", "in", activeEstimateStatuses)
        );
        const snap = await getDocs(fallbackQ);
        estimates = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      }

      setActiveEstimates(estimates);
      setActiveEstimatesCount(estimates.length);

      // --------------------
      // ACTIVE INVOICES (publicInvoices)
      // active if status is not paid/void and balance due is positive
      // total outstanding = sum of active invoice balances
      // --------------------
      const invoicesQ = query(
        collection(db, "publicInvoices"),
        orderBy("updatedAt", "desc")
      );

      let invoices = [];
      try {
        const invoicesSnap = await getDocs(invoicesQ);
        invoices = invoicesSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
      } catch (err) {
        // If you hit an index issue, the query itself is still correct — Firestore will give a link to create it.
        console.error("Invoices query failed:", err);
        // Fallback (no ordering) — still correct, avoids some index combos:
        const fallbackQ = query(collection(db, "publicInvoices"));
        const snap = await getDocs(fallbackQ);
        invoices = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      }

      invoices = invoices.filter(isOutstandingInvoice);

      const outstanding = invoices.reduce((sum, inv) => {
        return sum + safeNum(inv?.totals?.balanceDue ?? inv?.balanceDue);
      }, 0);

      setActiveInvoices(invoices);
      setActiveInvoicesCount(invoices.length);
      setTotalOutstanding(outstanding);

      // --------------------
      // UNPAID EXPENSES
      // --------------------
      const expenseQ = query(
        collection(db, "expenses"),
        where("paid", "==", false),
        orderBy("createdAt", "desc")
      );

      let expenses = [];
      try {
        const expenseSnap = await getDocs(expenseQ);
        expenses = expenseSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      } catch (err) {
        console.error("Expenses query failed:", err);
        const fallbackExpenseQ = query(collection(db, "expenses"));
        const expenseSnap = await getDocs(fallbackExpenseQ);
        expenses = expenseSnap.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .filter((expense) => expense.paid === false);
      }

      const totalUnpaid = expenses.reduce((sum, expense) => {
        return sum + safeNum(expense.amount);
      }, 0);

      setUnpaidExpenses(expenses);
      setUnpaidExpensesCount(expenses.length);
      setTotalUnpaidExpenses(totalUnpaid);
    };

    fetchDashboardData().catch((err) => {
      console.error("Dashboard fetch failed:", err);
    });
  }, []);

  return (
    <div className="admin-page">
      <AdminNav />
      <h1>Admin Dashboard</h1>

      {/* SUMMARY CARDS */}
      <div className="dashboard-top">
        <div className="dashboard-cards">
          <div className="dashboard-card">
            <h3>Total Leads</h3>
            <p>{leadCount ?? "-"}</p>
          </div>

          <div className="dashboard-card">
            <h3>New Leads</h3>
            <p>{newLeadCount ?? "-"}</p>
          </div>

          {/* NEW: Active Estimates tile */}
          <div
            className="dashboard-card"
            style={{ cursor: "pointer" }}
            onClick={() => navigate("/admin/estimates")}
            title="Go to Estimates"
          >
            <h3>Active Estimates</h3>
            <p>{activeEstimatesCount ?? "-"}</p>
            <div style={{ marginTop: 10, fontSize: 13, opacity: 0.9 }}>
              Status: sent / viewed
            </div>
          </div>

          {/* NEW: Active Invoices tile */}
          <div
            className="dashboard-card"
            style={{ cursor: "pointer" }}
            onClick={() => navigate("/admin/invoices")}
            title="Go to Invoices"
          >
            <h3>Active Invoices</h3>
            <p>{activeInvoicesCount ?? "-"}</p>
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 13, opacity: 0.9 }}>Outstanding</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>
                {money.format(totalOutstanding || 0)}
              </div>
            </div>
          </div>

          <div
            className="dashboard-card"
            style={{ cursor: "pointer" }}
            onClick={() => navigate("/admin/expenses")}
            title="Go to Expenses"
          >
            <h3>Unpaid Expenses</h3>
            <p>{unpaidExpensesCount ?? "-"}</p>
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 13, opacity: 0.9 }}>Total unpaid</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>
                {money.format(totalUnpaidExpenses || 0)}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="dashboard-grid">
        <div className="dashboard-col">
          {/* NEW: Active Estimates list */}
          <section className="dashboard-section dashboard-panel">
            <div className="dashboard-section-header">
              <h2>Active Estimates</h2>
              <Link to="/admin/estimates">View All</Link>
            </div>

            {!activeEstimates?.length ? (
              <p>No active estimates.</p>
            ) : (
              <div className="dashboard-table-wrap">
                <table className="dashboard-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Customer</th>
                      <th>Address</th>
                      <th>Total</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeEstimates.map((est) => {
                      const dt =
                        est.createdAt?.toDate?.() ||
                        est.sentAt?.toDate?.() ||
                        est.updatedAt?.toDate?.() ||
                        null;

                      const total =
                        est.totals?.grandTotal ??
                        est.totals?.total ??
                        est.grandTotal ??
                        est.total ??
                        0;

                      return (
                        <tr
                          key={est.id}
                          onClick={() => navigate(`/admin/estimates/edit/${est.id}`)}
                          style={{ cursor: "pointer" }}
                        >
                          <td>{dt ? dt.toLocaleDateString() : "-"}</td>
                          <td>{est.customer.companyName || est.customer.contacts.name || "-"}</td>
                          <td>{est.customer.billingAddress.line1 || est.customer.billingAddress.city || "-"}</td>
                          <td>{money.format(Number(total || 0))}</td>
                          <td>{est.status || "-"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* NEW: Active Invoices list */}
          <section className="dashboard-section dashboard-panel">
            <div className="dashboard-section-header">
              <h2>Active Invoices (Outstanding)</h2>
              <Link to="/admin/invoices">View All</Link>
            </div>

            {!activeInvoices?.length ? (
              <p>No invoices with an outstanding balance.</p>
            ) : (
              <div className="dashboard-table-wrap">
                <table className="dashboard-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Invoice #</th>
                      <th>Customer</th>
                      <th>Address</th>
                      <th>Balance Due</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeInvoices.map((inv) => {
                      const dt =
                        inv.createdAt?.toDate?.() ||
                        inv.issuedAt?.toDate?.() ||
                        inv.sentAt?.toDate?.() ||
                        null;

                      const invoiceNo =
                        inv.invoiceNumber || inv.number || inv.publicId || "-";

                      const balanceDue = Number(inv?.totals?.balanceDue ?? 0);

                      return (
                        <tr
                          key={inv.id}
                          onClick={() => navigate(`/admin/invoices/edit/${inv.id}`)}
                          style={{ cursor: "pointer" }}
                        >
                          <td>{dt ? dt.toLocaleDateString() : "-"}</td>
                          <td>{invoiceNo}</td>
                          <td>{inv.customer.companyName || inv.customer.contacts.name || "-"}</td>
                          <td>{inv.customer.billingAddress.line1 || inv.customer.billingAddress.city || "-"}</td>
                          <td>{money.format(balanceDue)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>

        <div className="dashboard-col dashboard-col--side">
          {/* RECENT LEADS */}
          <section className="dashboard-section dashboard-panel">
            <div className="dashboard-section-header">
              <h2>Recent Leads</h2>
              <Link to="/admin/leads">View All</Link>
            </div>

            {recentLeads.length === 0 ? (
              <p>No leads yet.</p>
            ) : (
              <div className="dashboard-table-wrap">
                <table className="dashboard-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Company</th>
                      <th>Name</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentLeads.map((lead) => (
                      <tr
                        key={lead.id}
                        onClick={() => navigate(`/admin/leads/${lead.id}`)}
                        style={{ cursor: "pointer" }}
                      >
                        <td>
                          {lead.createdAt
                            ? lead.createdAt.toDate().toLocaleDateString()
                            : "-"}
                        </td>
                        <td>{lead.company || "-"}</td>
                        <td>{lead.name}</td>
                        <td>
                          <StatusSelect
                            leadId={lead.id}
                            currentStatus={lead.status}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="dashboard-section dashboard-panel">
            <div className="dashboard-section-header">
              <h2>Unpaid Expenses</h2>
              <Link to="/admin/expenses">View All</Link>
            </div>

            {!unpaidExpenses?.length ? (
              <p>No unpaid expenses.</p>
            ) : (
              <div className="dashboard-table-wrap">
                <table className="dashboard-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Name</th>
                      <th>Amount</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unpaidExpenses.slice(0, 5).map((expense) => (
                      <tr key={expense.id}>
                        <td>{expense.createdAt?.toDate?.()?.toLocaleDateString() || "-"}</td>
                        <td>{expense.name || "-"}</td>
                        <td>{money.format(safeNum(expense.amount))}</td>
                        <td>{expense.description || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}


